import { atom, selector } from 'recoil';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import { VoxelSubRange } from '~/domain/services/imageService/gridGeometry';
import { sdfOperationInFlightState } from './structureSet/roiAtoms';

// ── The viewport's contour-deletion menu ─────────────────────────────

/**
 * Where the menu was asked for, so the item that answers can be worded
 * for it. Both routes offer the same actions on the same structure.
 */
export type ContourDeletionMenuSource = 'right-click' | 'hover-affordance';

/**
 * Everything the open menu will act on, SNAPSHOT at the moment it opened
 * rather than re-read as its items are clicked.
 *
 * That is the safety property. A menu describes a picture the user is
 * looking at: the slice that was on screen, the curve the pointer was
 * over, the view it was over. Re-reading any of those at click time would
 * let a scroll — or a layout change, or a rotation — land the deletion on
 * a slice the user never saw the menu over. Combined with closing the
 * menu on anything that invalidates the snapshot, it is impossible for an
 * item to act on a different slice than the one it was opened over.
 */
export interface ContourDeletionMenuState {
    /** Client coordinates the menu is anchored at. */
    anchorPosition: { top: number; left: number };
    source: ContourDeletionMenuSource;
    /**
     * The plane displayed by the view the menu was opened on — NOT the
     * main view. Right-clicking the sagittal side view while the main
     * view is transversal must not clear the transversal slice the user
     * did not click on.
     */
    planeAtOpen: PlaneMm | null;
    /**
     * The ROI the pointer was over, which may not be the selected one.
     * The menu always ACTS on the selection; this only decides whether it
     * also offers to select what was picked.
     */
    pickedRoiNumber: number | undefined;
    /**
     * The single voxel the pick resolved to, in VOXELS — never as a world
     * box, which the clear would have to convert back and round outward.
     * `undefined` when the geometry refuses the pick or no curve was
     * there, which is what disables "delete this curve" before any
     * labeling pass runs.
     */
    curveWindow: VoxelSubRange | undefined;
}

/** The open menu, or null. One at a time across every viewport. */
export const contourDeletionMenuState = atom<ContourDeletionMenuState | null>({
    key: 'contourDeletionMenuState',
    default: null,
});

/**
 * A contour write is in flight that {@link sdfOperationInFlightState}
 * cannot see: the rectangle's labeling pass runs before the tracked write
 * is entered, and the extent clears never go through it at all.
 */
export const contourWriteInFlightState = atom<boolean>({
    key: 'contourWriteInFlightState',
    default: false,
});

/**
 * "A write to a structure is in flight", from either path.
 *
 * The one fact every surface that must not start a second write reads —
 * the menu's items, the panel's apply row, the toolbar's rectangle
 * button — so they cannot disagree about whether the structure is busy.
 */
export const structureWriteInFlightState = selector<boolean>({
    key: 'structureWriteInFlightState',
    get: ({ get }) =>
        get(sdfOperationInFlightState) || get(contourWriteInFlightState),
});
